<template>
    <div>
        <!-- Turn off user-level auto-ESO confirmation -->
        <TransitionRoot as="template" :show="showUserAutoEsoDisableModal">
            <Dialog as="div" class="relative z-50" @close="showUserAutoEsoDisableModal = false">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>
                <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
                                <div>
                                    <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
                                        <font-awesome-icon :icon="faCircleExclamation" class="h-6 w-6 text-amber-600" aria-hidden="true" />
                                    </div>
                                    <div class="mt-3 text-center sm:mt-5">
                                        <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">
                                            Turn off automatic enhanced screening?
                                        </DialogTitle>
                                        <div class="mt-2 text-sm text-gray-600 text-left">
                                            <p>
                                                This stops your account-wide setting for all properties. You can turn it back on anytime. Per-property settings in the database are not changed.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div class="mt-5 sm:mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-3">
                                    <button
                                        type="button"
                                        class="w-full justify-center rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 sm:w-auto"
                                        @click="showUserAutoEsoDisableModal = false"
                                    >
                                        Keep it on
                                    </button>
                                    <button
                                        type="button"
                                        class="w-full justify-center rounded-md border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-700 shadow-sm hover:bg-red-50 sm:w-auto"
                                        :disabled="userAutoEsoLoading"
                                        @click="confirmTurnOffUserAutoEso"
                                    >
                                        <span v-if="userAutoEsoLoading">Turning off…</span>
                                        <span v-else>Turn off</span>
                                    </button>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </TransitionRoot>

        <!-- User-level: enable options (payer) -->
        <TransitionRoot as="template" :show="showUserAutoEsoConfirmModal">
            <Dialog as="div" class="relative z-50" @close="closeUserAutoEsoConfirmModal">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>
                <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
                                <div>
                                    <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                                        <font-awesome-icon :icon="faShieldCheck" class="h-6 w-6 text-blue-600" aria-hidden="true" />
                                    </div>
                                    <div class="mt-3 text-center sm:mt-5">
                                        <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Enable Automatic Verified Screening (all properties)</DialogTitle>
                                        <div class="mt-3 text-left space-y-4">
                                            <p class="text-sm text-gray-600">Set your risk level once. Protect yourself and every applicant automatically across all of your properties.</p>
                                            <fieldset class="space-y-3">
                                                <legend class="sr-only">Who pays for enhanced screening</legend>
                                                <label class="flex gap-3 cursor-pointer items-start rounded-md border border-gray-200 p-3 has-[:checked]:border-rent-risk-blue-400 has-[:checked]:bg-blue-50/50">
                                                    <input v-model="userAutoEsoPayerChoice" type="radio" :value="AUTO_ESO_PAYER_AGENT" class="mt-1 h-4 w-4 border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-500" />
                                                    <span class="text-sm text-gray-700">I pay for verified screening for each applicant.</span>
                                                </label>
                                                <label v-if="userAutoEsoPayerChoice === AUTO_ESO_PAYER_AGENT" class="flex gap-3 cursor-pointer items-start pl-1">
                                                    <input v-model="userAutoEsoAgentAck" type="checkbox" class="mt-0.5 h-4 w-4 rounded border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-500" />
                                                    <span class="text-sm text-gray-600">I understand that I will be billed ${{ protectDiscountedTotal }} each time a new applicant starts an application (RiskGuard Pro subscribers get 2 free monthly reports and will be billed ${{ protectDiscountedTotal }}/report after their 2 free enhanced reports).</span>
                                                </label>
                                                <label class="flex gap-3 cursor-pointer items-start rounded-md border border-gray-200 p-3 has-[:checked]:border-rent-risk-blue-400 has-[:checked]:bg-blue-50/50">
                                                    <input v-model="userAutoEsoPayerChoice" type="radio" :value="AUTO_ESO_PAYER_APPLICANT" class="mt-1 h-4 w-4 border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-500" />
                                                    <span class="text-sm text-gray-700">Request that each applicant verify their own report and pay ${{ protectDiscountedTotal }}.</span>
                                                </label>
                                                <label v-if="userAutoEsoPayerChoice === AUTO_ESO_PAYER_APPLICANT" class="flex gap-3 cursor-pointer items-start pl-1">
                                                    <input v-model="userAutoEsoApplicantAck" type="checkbox" class="mt-0.5 h-4 w-4 rounded border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-500" />
                                                    <span class="text-sm text-gray-600">I understand that applicants will be asked to pay ${{ protectDiscountedTotal }} and complete enhanced screening unless they remove it at checkout.</span>
                                                </label>
                                            </fieldset>
                                        </div>
                                    </div>
                                </div>
                                <div class="mt-5 sm:mt-6 flex gap-2">
                                    <button type="button" class="flex-1 justify-center rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600" @click="closeUserAutoEsoConfirmModal">Cancel</button>
                                    <button type="button" :disabled="userAutoEsoLoading || !canSubmitUserAutoEsoEnable" @click="submitUserAutoEsoEnable" class="flex-1 justify-center rounded-md bg-rent-risk-blue-400 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rent-risk-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50">
                                        <span v-if="userAutoEsoLoading">Saving...</span>
                                        <span v-else>Continue</span>
                                    </button>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </TransitionRoot>

        <!-- RAC-10413: landlord-paid auto-ESO no longer requires a RiskGuard subscription,
             only a card on file to charge the per-applicant fee. The plan-picker modal is gone;
             landlords without a card add one here and auto-ESO enables once the card lands. -->
        <AddCardPopup
            v-if="isValidStripeData"
            :key="normalizedStripeData?.publishable_key"
            :show="showUserAutoEsoAddCardModal"
            :stripe_data="normalizedStripeData"
            heading="Enable Automatic Verified Screening"
            :notice-text="`A card on file is required for landlord-paid screening. It will be charged $${protectDiscountedTotal} each time a new applicant starts an application.`"
            confirm-button-text="Add Card &amp; Enable"
            @card-added="handleUserAutoEsoCardAdded"
            @close="showUserAutoEsoAddCardModal = false"
        />
    </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { router } from '@inertiajs/vue3'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faShieldCheck, faCircleExclamation } from '@fortawesome/pro-light-svg-icons'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import AddCardPopup from '@/Shared/AddCardPopup.vue'

const route = (typeof window !== 'undefined' && window.route) || ((name, ...params) => {
    console.warn('Route function not available')
    return '#'
})

const protectDiscountedTotal = import.meta.env.VITE_ADDONS_DISCOUNTED_PRICE_TOTAL ?? '29'
const AUTO_ESO_PAYER_AGENT = 1
const AUTO_ESO_PAYER_APPLICANT = 2

const props = defineProps({
    // NOTE: must be camelCase — parents bind `:user-global-auto-eso`, which Vue
    // normalizes to `userGlobalAutoEso`. The old snake_case declaration never
    // matched, so the component always saw the default ({is_active: false}) and
    // open() could only ever reach the ENABLE modal — disable was unreachable.
    userGlobalAutoEso: {
        type: Object,
        default: () => ({
            is_active: false,
            payer: null,
            effective_billing: false,
            payment_method_required_but_missing: false,
        }),
    },
    is_agent_payment_method_added: { type: Boolean, default: false },
    user_payment_method: { type: Object, default: null },
    stripe_data: { type: [Object, Array], default: () => [] },
    // 86bc70q8c: Settings keeps unsaved Credit/Income edits across the save.
    preserveState: { type: Boolean, default: false },
})

const showUserAutoEsoDisableModal = ref(false)
const showUserAutoEsoConfirmModal = ref(false)
const showUserAutoEsoAddCardModal = ref(false)
const userAutoEsoLoading = ref(false)
const userAutoEsoPayerChoice = ref(null)
const userAutoEsoAgentAck = ref(false)
const userAutoEsoApplicantAck = ref(false)

const isActive = computed(() => {
    const eso = props.userGlobalAutoEso
    return Boolean(eso?.is_active) || eso?.payer != null
})

const canSubmitUserAutoEsoEnable = computed(() => {
    if (userAutoEsoPayerChoice.value === AUTO_ESO_PAYER_AGENT) {
        return userAutoEsoAgentAck.value
    }
    if (userAutoEsoPayerChoice.value === AUTO_ESO_PAYER_APPLICANT) {
        return userAutoEsoApplicantAck.value
    }
    return false
})

const normalizedStripeData = computed(() => {
    const data = props.stripe_data
    if (Array.isArray(data) || !data || typeof data !== 'object') {
        return null
    }
    return data.publishable_key ? data : null
})

const isValidStripeData = computed(() => {
    return normalizedStripeData.value !== null &&
           normalizedStripeData.value.publishable_key &&
           normalizedStripeData.value.setup_intent
})

const openEnable = () => {
    userAutoEsoPayerChoice.value = null
    userAutoEsoAgentAck.value = false
    userAutoEsoApplicantAck.value = false
    showUserAutoEsoConfirmModal.value = true
}

const openDisable = () => {
    showUserAutoEsoDisableModal.value = true
}

const open = () => {
    if (isActive.value) {
        openDisable()
        return
    }
    openEnable()
}

const confirmTurnOffUserAutoEso = () => {
    userAutoEsoLoading.value = true
    showUserAutoEsoDisableModal.value = false
    router.put(
        route('user.auto-eso.update'),
        { enabled: false },
        {
            preserveScroll: true,
            preserveState: props.preserveState,
            onFinish: () => {
                userAutoEsoLoading.value = false
            },
        }
    )
}

const closeUserAutoEsoConfirmModal = () => {
    showUserAutoEsoConfirmModal.value = false
    userAutoEsoPayerChoice.value = null
    userAutoEsoAgentAck.value = false
    userAutoEsoApplicantAck.value = false
}

const submitUserAutoEsoEnable = () => {
    if (!canSubmitUserAutoEsoEnable.value || userAutoEsoPayerChoice.value == null) {
        return
    }
    if (userAutoEsoPayerChoice.value === AUTO_ESO_PAYER_APPLICANT) {
        userAutoEsoLoading.value = true
        showUserAutoEsoConfirmModal.value = false
        router.put(
            route('user.auto-eso.update'),
            { enabled: true, payer: userAutoEsoPayerChoice.value },
            {
                preserveScroll: true,
                preserveState: props.preserveState,
                onFinish: () => {
                    userAutoEsoLoading.value = false
                },
            }
        )
        return
    }
    if (userAutoEsoPayerChoice.value === AUTO_ESO_PAYER_AGENT) {
        // RAC-10413: a card on file (not a subscription) is what landlord-paid
        // screening requires — the per-applicant fee is charged to that card.
        if (!props.is_agent_payment_method_added) {
            if (!isValidStripeData.value) {
                alert('Unable to add card. Please refresh the page and try again.')
                return
            }
            showUserAutoEsoConfirmModal.value = false
            showUserAutoEsoAddCardModal.value = true
            return
        }
        enableUserAutoEsoAgentPaid()
    }
}

const enableUserAutoEsoAgentPaid = () => {
    userAutoEsoLoading.value = true
    showUserAutoEsoConfirmModal.value = false
    router.put(
        route('user.auto-eso.update'),
        { enabled: true, payer: AUTO_ESO_PAYER_AGENT },
        {
            preserveScroll: true,
            preserveState: props.preserveState,
            onFinish: () => {
                userAutoEsoLoading.value = false
            },
        }
    )
}

const handleUserAutoEsoCardAdded = ({ success }) => {
    showUserAutoEsoAddCardModal.value = false
    if (!success) {
        alert('Failed to add card. Please try again.')
        return
    }
    enableUserAutoEsoAgentPaid()
}

defineExpose({ open, openEnable, openDisable })
</script>
